import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ExtractedData, ExtractedField } from '@app/_models/extracted-data.model';
import { FieldMapping, FormPopulationSettings } from '@app/_models/field-mapping.model';

@Injectable({
  providedIn: 'root'
})
export class FormPopulationService {
  
  constructor() { }

  /**
   * Populate form with extracted data
   */
  populateForm(
    form: UntypedFormGroup, 
    extractedData: ExtractedData, 
    settings: FormPopulationSettings
  ): { populated: string[], skipped: string[], conflicts: string[] } {
    const result = {
      populated: [] as string[],
      skipped: [] as string[],
      conflicts: [] as string[]
    };

    // Process each mapping
    settings.mappings.forEach(mapping => {
      if (!mapping.isSelected) {
        result.skipped.push(mapping.formFieldName);
        return;
      }

      const extractedField = this.getExtractedField(extractedData, mapping.extractedFieldName);
      
      if (!extractedField || extractedField.confidence < settings.minimumConfidence) {
        result.skipped.push(mapping.formFieldName);
        return;
      }

      const formControl = form.get(mapping.formFieldName);
      if (!formControl) {
        result.skipped.push(mapping.formFieldName);
        return;
      }

      // Check for conflicts
      const hasExistingValue = formControl.value && formControl.value.toString().trim() !== '';
      
      if (hasExistingValue && settings.preserveExistingData) {
        if (mapping.conflictResolution === 'skip') {
          result.conflicts.push(mapping.formFieldName);
          return;
        }
        if (mapping.conflictResolution === 'merge') {
          // TODO: Implement merge logic
          result.conflicts.push(mapping.formFieldName);
          return;
        }
      }

      // Populate the field
      try {
        const transformedValue = this.transformValueForFormField(
          extractedField.value, 
          mapping.formFieldName
        );
        
        formControl.setValue(transformedValue);
        result.populated.push(mapping.formFieldName);
      } catch (error) {
        console.error(`Error populating field ${mapping.formFieldName}:`, error);
        result.skipped.push(mapping.formFieldName);
      }
    });

    return result;
  }

  /**
   * Generate automatic field mappings based on extracted data
   */
  generateAutomaticMappings(extractedData: ExtractedData): FieldMapping[] {
    const mappings: FieldMapping[] = [];
    
    // Direct mappings
    const directMappings = this.getDirectFieldMappings();
    
    Object.keys(directMappings).forEach(extractedField => {
      const field = this.getExtractedField(extractedData, extractedField);
      if (field && field.confidence > 30) {
        mappings.push({
          formFieldName: directMappings[extractedField],
          extractedFieldName: extractedField,
          confidence: field.confidence,
          isManualMapping: false,
          isSelected: field.confidence > 60, // Auto-select high confidence fields
          conflictResolution: 'skip'
        });
      }
    });

    return mappings.sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Get direct field mappings between extracted data and form fields
   */
  private getDirectFieldMappings(): { [key: string]: string } {
    return {
      'vehicleMake': 'vehicleMake',
      'model': 'model',
      'variant': 'variant',
      'registrationNumber': 'registrationNumber',
      'chasisNumber': 'chasisNumber',
      'engineNumber': 'engineNumber',
      'vehicleYear': 'vehicleYear',
      'vehicleType': 'vehicleType',
      'firstName': 'firstName',
      'middleName': 'middleName',
      'lastName': 'lastName',
      'email': 'email',
      'contactNumber': 'contactNumber',
      'gender': 'gender',
      'dob': 'dob',
      'age': 'age',
      'address': 'address',
      'state': 'state',
      'city': 'city',
      'nomineeName': 'nomineeName',
      'nomineeRelationship': 'nomineeRelationship',
      'nomineeGender': 'nomineeGender',
      'nomineeAge': 'nomineeAge',
      'financeCompanyName': 'financeCompanyName',
      'financeCoverAmount': 'financeCoverAmount',
      'planAmount': 'planAmount',
      'planGSTAmount': 'planGSTAmount',
      'planTotalAmount': 'planTotalAmount',
      'startDate': 'startDate',
      'endDate': 'endDate',
      'issueDate': 'issueDate',
      'gstin': 'gstin',
      'clientType': 'clientType'
    };
  }

  /**
   * Get extracted field by name
   */
  private getExtractedField(extractedData: ExtractedData, fieldName: string): ExtractedField | null {
    return (extractedData as any)[fieldName] || null;
  }

  /**
   * Transform extracted value for specific form fields
   */
  private transformValueForFormField(value: string, formFieldName: string): any {
    switch (formFieldName) {
      case 'vehicleMake':
        return this.transformVehicleMake(value);
      
      case 'vehicleType':
        return this.transformVehicleType(value);
      
      case 'gender':
      case 'nomineeGender':
        return this.transformGender(value);
      
      case 'clientType':
        return this.transformClientType(value);
      
      case 'dob':
      case 'startDate':
      case 'endDate':
      case 'issueDate':
        return this.transformDate(value);
      
      case 'contactNumber':
        return this.transformPhoneNumber(value);
      
      case 'email':
        return this.transformEmail(value);
      
      case 'registrationNumber':
        return this.transformRegistrationNumber(value);
      
      case 'age':
      case 'nomineeAge':
      case 'vehicleYear':
        return this.transformNumeric(value);
      
      case 'planAmount':
      case 'planGSTAmount':
      case 'planTotalAmount':
      case 'financeCoverAmount':
        return this.transformAmount(value);
      
      case 'firstName':
      case 'middleName':
      case 'lastName':
      case 'nomineeName':
        return this.transformName(value);
      
      case 'address':
        return this.transformAddress(value);
      
      case 'state':
      case 'city':
        return this.transformLocation(value);
      
      default:
        return value.trim();
    }
  }

  /**
   * Transform vehicle make to match dropdown options
   */
  private transformVehicleMake(value: string): string {
    const normalized = value.toUpperCase().trim();
    
    // Common brand mappings
    const brandMappings: { [key: string]: string } = {
      'MARUTI': 'MARUTI SUZUKI',
      'SUZUKI': 'MARUTI SUZUKI',
      'TATA MOTORS': 'TATA',
      'MAHINDRA & MAHINDRA': 'MAHINDRA',
      'BAJAJ AUTO': 'BAJAJ',
      'HERO MOTOCORP': 'HERO',
      'TVS MOTOR': 'TVS'
    };

    return brandMappings[normalized] || value;
  }

  /**
   * Transform vehicle type
   */
  private transformVehicleType(value: string): string {
    const normalized = value.toLowerCase();
    
    if (normalized.includes('two') || normalized.includes('bike') || normalized.includes('scooter')) {
      return 'Two Wheeler';
    }
    if (normalized.includes('car') || normalized.includes('four')) {
      return 'Four Wheeler';
    }
    
    return 'Two Wheeler'; // Default
  }

  /**
   * Transform gender
   */
  private transformGender(value: string): string {
    const normalized = value.toLowerCase();
    
    if (normalized.includes('male') && !normalized.includes('female')) {
      return 'Male';
    }
    if (normalized.includes('female')) {
      return 'Female';
    }
    if (normalized.includes('other') || normalized.includes('third')) {
      return 'Other';
    }
    
    return value;
  }

  /**
   * Transform client type
   */
  private transformClientType(value: string): string {
    const normalized = value.toLowerCase();
    
    if (normalized.includes('individual') || normalized.includes('personal')) {
      return 'Individual';
    }
    if (normalized.includes('corporate') || normalized.includes('company') || normalized.includes('business')) {
      return 'Corporate';
    }
    
    return 'Individual'; // Default
  }

  /**
   * Transform date strings to Angular date format
   */
  private transformDate(value: string): any {
    try {
      const dateRegex = /(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{2,4})/;
      const match = value.match(dateRegex);
      
      if (match) {
        let day = parseInt(match[1]);
        let month = parseInt(match[2]);
        let year = parseInt(match[3]);
        
        // Handle 2-digit years
        if (year < 100) {
          year += year < 50 ? 2000 : 1900;
        }
        
        // Return NgbDateStruct format
        return {
          year: year,
          month: month,
          day: day
        };
      }
    } catch (error) {
      console.error('Date transformation error:', error);
    }
    
    return null;
  }

  /**
   * Transform phone number
   */
  private transformPhoneNumber(value: string): string {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Remove country code if present
    if (digits.startsWith('91') && digits.length === 12) {
      return digits.substring(2);
    }
    
    return digits;
  }

  /**
   * Transform email
   */
  private transformEmail(value: string): string {
    return value.toLowerCase().trim();
  }

  /**
   * Transform registration number
   */
  private transformRegistrationNumber(value: string): string {
    // Remove extra spaces and standardize format
    return value.replace(/\s+/g, '').toUpperCase();
  }

  /**
   * Transform numeric values
   */
  private transformNumeric(value: string): number {
    const numeric = parseFloat(value.replace(/[^\d.]/g, ''));
    return isNaN(numeric) ? 0 : numeric;
  }

  /**
   * Transform amount values
   */
  private transformAmount(value: string): number {
    // Remove currency symbols and commas
    const cleanValue = value.replace(/[₹,Rs\.INR\s]/g, '');
    const numeric = parseFloat(cleanValue);
    return isNaN(numeric) ? 0 : numeric;
  }

  /**
   * Transform name fields
   */
  private transformName(value: string): string {
    // Convert to title case
    return value.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()).trim();
  }

  /**
   * Transform address
   */
  private transformAddress(value: string): string {
    // Clean up address format
    return value.replace(/\s+/g, ' ').trim();
  }

  /**
   * Transform location (state/city)
   */
  private transformLocation(value: string): string {
    // Convert to title case and clean
    return value.toLowerCase().replace(/\b\w/g, char => char.toUpperCase()).trim();
  }

  /**
   * Validate form population result
   */
  validatePopulationResult(form: UntypedFormGroup, populatedFields: string[]): {
    valid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    populatedFields.forEach(fieldName => {
      const control = form.get(fieldName);
      if (control && control.errors) {
        errors.push(`${fieldName}: ${Object.keys(control.errors).join(', ')}`);
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }
}